"use client";

import { useMemo } from "react";
import { useTripData } from "@/lib/trip-data-context";
import { formatDay } from "@/lib/format";
import { Card, InnerCard, Badge, TabLoading, EmptyState } from "@/components/trip/ui";
import type { Flight } from "@/lib/types";

const legLabels: Record<Flight["leg"], string> = {
  outbound: "Outbound",
  inbound: "Return",
  positioning: "Positioning",
};

function formatTime(timeStr: string): string {
  const d = new Date(timeStr);
  return d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

type TransitItem =
  | { kind: "flight"; flight: Flight; sortDate: string }
  | { kind: "car"; sortDate: string };

export default function TransitPage() {
  const { data, loading } = useTripData();

  const items = useMemo(() => {
    if (!data) return [];
    const result: TransitItem[] = [];

    for (const flight of data.flights) {
      const sortDate = flight.segments[0]?.departure.time ?? "";
      result.push({ kind: "flight", flight, sortDate });
    }

    if (data.rentalCar) {
      result.push({ kind: "car", sortDate: data.rentalCar.pickupDate });
    }

    result.sort((a, b) => a.sortDate.localeCompare(b.sortDate));
    return result;
  }, [data]);

  if (loading || !data) {
    return <TabLoading />;
  }

  const car = data.rentalCar;

  if (items.length === 0) {
    return <EmptyState>No transit booked yet.</EmptyState>;
  }

  return (
    <div>
      {items.map((item) => {
        if (item.kind === "flight") {
          const flight = item.flight;
          return (
            <Card key={flight.id}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{legLabels[flight.leg]}</div>
                <Badge>BOOKED</Badge>
              </div>
              {flight.segments.map((seg, i) => (
                <InnerCard key={i} style={{ marginBottom: i < flight.segments.length - 1 ? 8 : 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{seg.flightNumber || seg.airline}</span>
                    <span style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)" }}>{seg.airline}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 4 }}>
                    <strong>{seg.departure.airport}</strong> → <strong>{seg.arrival.airport}</strong>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text3)", fontFamily: "var(--mono)" }}>
                    {formatTime(seg.departure.time)} → {formatTime(seg.arrival.time)}
                  </div>
                  {seg.confirmationNumber && (
                    <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
                      Conf: {seg.confirmationNumber}
                    </div>
                  )}
                  {seg.seatSelection && (
                    <div style={{ fontSize: 11, color: "var(--text3)" }}>
                      Seats: {seg.seatSelection}
                    </div>
                  )}
                </InnerCard>
              ))}
            </Card>
          );
        }

        // Rental car
        if (!car) return null;
        return (
          <Card key="car">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{car.company}</div>
              <Badge>BOOKED</Badge>
            </div>
            <InnerCard>
              <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 6 }}>
                <strong>{car.vehicle}</strong>
              </div>
              <div style={{ fontSize: 12, color: "var(--text3)", fontFamily: "var(--mono)", marginBottom: 8 }}>
                {formatDay(car.pickupDate)} → {formatDay(car.dropoffDate)}
              </div>
              <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 2 }}>
                Pickup: {car.pickupLocation}
              </div>
              <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 6 }}>
                {car.pickupContact}
              </div>
              <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 2 }}>
                Drop-off: {car.dropoffLocation}
              </div>
              <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 6 }}>
                {car.dropoffContact}
              </div>
              <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
                Conf: {car.confirmationNumber} · {car.cost}
              </div>
            </InnerCard>
          </Card>
        );
      })}
    </div>
  );
}
